import type { RoofMesh, RoofPitchData, RoofProductionInformation } from "../@types/types";
import { AlertType } from "../@types/enums";
import { useViewerStore } from "../store/store";

export const LIGHTREACH_WARNING_TITLE = "LightReach Warning";
export const LIGHTREACH_MIN_PITCH = 5;
export const LIGHTREACH_MAX_PITCH = 50;

export const getLightReachPitchWarningMessage = (
    minPitch = LIGHTREACH_MIN_PITCH,
    maxPitch = LIGHTREACH_MAX_PITCH,
): string => `LightReach will only accept designs on roof between ${minPitch}° and ${maxPitch}° pitch.`;

export const isLightReachFinancePartner = (partnerName?: string | null): boolean =>
    (partnerName ?? "").toLowerCase().trim().includes("lightreach");

export const isPitchAtOrBelowLightReachMin = (
    pitch: number,
    minPitch = LIGHTREACH_MIN_PITCH,
): boolean => Number(pitch) <= minPitch;

export const isPitchOutsideLightReachRange = (
    pitch: number,
    minPitch = LIGHTREACH_MIN_PITCH,
    maxPitch = LIGHTREACH_MAX_PITCH,
): boolean => Number(pitch) <= minPitch || Number(pitch) >= maxPitch;

type ViewerAlertTarget = {
    viewerAlert: (config: Record<string, unknown>) => void;
    removeViewerAlert?: () => void;
} | null | undefined;

type SceneViewerForLightReach = {
    roofMeshArr?: RoofMesh[];
    roofs?: Array<{
        faces?: Array<{
            roof_tag_id?: number;
            roof_number?: number;
            roof_number_from_aurora?: number;
            pitchOverride?: number;
            oriAng?: { tilt?: number };
        }>;
    }>;
};

const roofIdFromMesh = (mesh: RoofMesh): string | number | undefined =>
    mesh.userData.roof_tag_id ??
    mesh.userData.roof_number_from_aurora ??
    mesh.roofFaceId;

/** Effective pitch per roof — matches ThreeDViewer / Roof Efficiency table (roofInfoArr overrides when present). */
export const buildLightReachPitchSource = (
    sceneViewer: SceneViewerForLightReach | null | undefined,
    roofInfoArr: RoofProductionInformation[],
    roofPitchOverrideArr: RoofPitchData[] | undefined,
): Array<{ roofId: string | number | undefined; pitch: number }> => {
    const roofMeshes = sceneViewer?.roofMeshArr;
    const faces = sceneViewer?.roofs?.[0]?.faces;
    const roofInfoByFaceId = new Map(roofInfoArr.map((r) => [r.roofFaceId, r]));

    if (roofMeshes?.length) {
        return roofMeshes.map((mesh) => {
            const info = roofInfoByFaceId.get(mesh.roofFaceId);
            const pitch = info
                ? Math.round(Number(info.pitchOverride ?? info.pitch))
                : Math.round(Number(mesh.userData.pitchOverride ?? mesh.oriAng?.tilt ?? 0));
            return { roofId: roofIdFromMesh(mesh), pitch };
        });
    }

    if (faces?.length) {
        return faces.map((face) => ({
            roofId: face.roof_tag_id ?? face.roof_number_from_aurora ?? face.roof_number,
            pitch: Math.round(Number(face.pitchOverride ?? face.oriAng?.tilt ?? 0)),
        }));
    }

    return (roofPitchOverrideArr ?? []).map((r) => ({
        roofId: r.roofId,
        pitch: Math.round(Number(r.pitch)),
    }));
};

export const formatLightReachOutOfRangeRoofList = (
    entries: Array<{ roofId: string | number | null | undefined; pitch: number }>,
): string =>
    entries
        .map(({ roofId, pitch }) => {
            const idLabel = roofId != null && String(roofId) !== "" ? String(roofId) : "?";
            return `Roof ${idLabel} (${pitch}°)`;
        })
        .join(", ");

const buildLightReachViolationKey = (
    entries: Array<{ roofId: string | number | null | undefined; pitch: number }>,
): string =>
    entries
        .map(({ roofId, pitch }) => `${roofId ?? "?"}:${pitch}`)
        .sort()
        .join("|");

export const markLightReachWarningDismissedByUser = (): void => {
    const { viewerAlert, lightReachViolationKey } = useViewerStore.getState();
    const title = viewerAlert?.title;

    if (typeof title !== "string" || title !== LIGHTREACH_WARNING_TITLE) {
        return;
    }

    useViewerStore.setState({
        isLightReachWarningDismissedByUser: true,
        lightReachDismissedViolationKey: lightReachViolationKey,
    });
};

/** Clears the viewer alert only when it is the LightReach pitch warning (avoids closing unrelated modals). */
export const dismissLightReachPitchWarningIfShowing = (): void => {
    const { viewerAlert, sceneViewer } = useViewerStore.getState();
    if (!viewerAlert?.show) return;
    const title = viewerAlert.title;
    if (typeof title !== "string" || title !== LIGHTREACH_WARNING_TITLE) return;
    sceneViewer?.removeViewerAlert?.();
};

export const showLightReachPitchWarning = (
    sceneViewer: ViewerAlertTarget,
    options?: {
        isModal?: boolean;
        isCloseAfterDisplay?: boolean;
        autoCloseSeconds?: number;
        message?: unknown;
        messageType?: unknown;
        minPitch?: number;
        maxPitch?: number;
    },
): void => {
    if (!sceneViewer?.viewerAlert) return;

    const minPitch = options?.minPitch ?? LIGHTREACH_MIN_PITCH;
    const maxPitch = options?.maxPitch ?? LIGHTREACH_MAX_PITCH;

    sceneViewer.viewerAlert({
        show: true,
        title: LIGHTREACH_WARNING_TITLE,
        message: options?.message ?? getLightReachPitchWarningMessage(minPitch, maxPitch),
        messageType: options?.messageType ?? "warning",
        ...(options?.isModal !== undefined ? { isModal: options.isModal } : {}),
        isCloseAfterDisplay:  false,
    });
};

/**
 * Single source of truth for the site-wide LightReach pitch warning (rule text + “Out of range: Roof …” list).
 * Call after ground-mount select, pitch edits, design load, etc.
 */
export const syncLightReachSitePitchWarning = (sceneViewer: ViewerAlertTarget): void => {
    if (!sceneViewer?.viewerAlert) return;

    const { financePartnerDetails, roofInfoArr, roofPitchOverrideArr } = useViewerStore.getState();

    if (!isLightReachFinancePartner(financePartnerDetails?.name)) {
        useViewerStore.setState({
            isLightReachWarningDismissedByUser: false,
            lightReachDismissedViolationKey: "",
            lightReachViolationKey: "",
        });
        dismissLightReachPitchWarningIfShowing();
        return;
    }

    const pitchSource = buildLightReachPitchSource(
        sceneViewer as SceneViewerForLightReach,
        roofInfoArr ?? [],
        roofPitchOverrideArr,
    );
    const outOfRange = pitchSource.filter(({ pitch }) => isPitchOutsideLightReachRange(pitch));

    if (outOfRange.length === 0) {
        useViewerStore.setState({
            isLightReachWarningDismissedByUser: false,
            lightReachDismissedViolationKey: "",
            lightReachViolationKey: "",
        });
        dismissLightReachPitchWarningIfShowing();
        return;
    }

    const violationKey = buildLightReachViolationKey(outOfRange);
    useViewerStore.setState({ lightReachViolationKey: violationKey });

    const { isLightReachWarningDismissedByUser, lightReachDismissedViolationKey } = useViewerStore.getState();
    if (isLightReachWarningDismissedByUser && lightReachDismissedViolationKey === violationKey) {
        return;
    }

    const { viewerAlert } = useViewerStore.getState();
    if (viewerAlert?.show && viewerAlert.isModal) {
        // Do not replace an open modal the toast is non-blocking and will sync again when the modal closes.
        return;
    }
    if (
        viewerAlert?.show &&
        !viewerAlert.isModal &&
        viewerAlert.messageType === AlertType.success
    ) {
        // sync again after it is dismissed.
        return;
    }

    const roofList = formatLightReachOutOfRangeRoofList(outOfRange);
    const message = `${getLightReachPitchWarningMessage()}\n\nOut of range: ${roofList}.`;

    showLightReachPitchWarning(sceneViewer, {
        message,
        messageType: "warning",
        isModal: false,
        isCloseAfterDisplay: false,
    });
    useViewerStore.setState({
        isLightReachWarningDismissedByUser: false,
        lightReachDismissedViolationKey: "",
    });
};
